import {
  Dialog,
  Button,
  LoadingSpinner,
  PlainButton,
  customerPhoneStringToInputValue,
} from "@jugl-web/ui-kit";
import { useTranslations, cx } from "@jugl-web/utils";
import { Customer, FormField, useRestApiProvider } from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useMemo, useCallback, useEffect, useRef } from "react";
import { CustomerFormField } from "@jugl-web/domain-resources/customers/components/CustomerFormField/CustomerFormField";
import { useForm, FieldValues, FieldError } from "react-hook-form";
import { ReactComponent as CloseIcon } from "@jugl-web/ui-kit/overlay/DrawerHeader/assets/close.svg";
import { processCustomerFormsToApi } from "@jugl-web/domain-resources/customers/utils/processCustomerFormsToApi";
import { useCustomerFields } from "@jugl-web/domain-resources/customers/hooks/useCustomerFields";

type CustomerFormValues = { [key: string]: string };

export const AddNewCustomerDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (customer: Customer) => void;
}> = ({ isOpen, onClose, onSuccess }) => {
  const {
    formState,
    control,
    reset,
    watch,
    handleSubmit,
    register,
    getFieldState,
  } = useForm<CustomerFormValues>({
    mode: "all",
    shouldUnregister: true,
    resetOptions: {
      keepSubmitCount: false,
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);
  const { customersApi } = useRestApiProvider();
  const [addNewCustomer, { isLoading: isCreatingNewCustomer }] =
    customersApi.useAddCustomerMutation();

  const formParams = watch();
  const initialFocus = useRef<HTMLInputElement>(null);
  const { entity } = useEntitySelectedProvider();
  const { t } = useTranslations();
  const { formFields, isLoading } = useCustomerFields({
    entityId: entity.id,
  });

  const isFieldInvalid = useCallback(
    (id: string) => {
      const state = getFieldState(id);
      return !formParams[id] || state.invalid;
    },
    [formParams, getFieldState]
  );

  const fieldsData: { [key: string]: FormField | undefined } = useMemo(() => {
    if (!formFields) return {};
    return {
      firstName: {
        ...formFields?.filter(
          (field) =>
            field.name === "first_name" &&
            field.is_cust_field === false &&
            field.section === "personal"
        )[0],
      },
      email: {
        ...formFields?.filter(
          (field) =>
            field.name === "email" &&
            field.is_cust_field === false &&
            field.section === "personal"
        )[0],
      },
      mobile: {
        ...formFields?.filter(
          (field) =>
            field.name === "phone_number" &&
            field.is_cust_field === false &&
            field.section === "personal"
        )[0],
      },
    };
  }, [formFields]);

  const fieldValues = useMemo(() => {
    const firstNameValue =
      fieldsData.firstName?.id && formParams[fieldsData.firstName.id];
    const emailValue = fieldsData.email?.id && formParams[fieldsData.email.id];
    const mobileValue =
      fieldsData.mobile?.id && formParams[fieldsData.mobile.id];
    return {
      firstName: firstNameValue,
      email: emailValue,
      mobile: mobileValue,
    };
  }, [fieldsData, formParams]);

  const shouldShowAddPhoneOrMail = useMemo(() => {
    const { email, mobile } = fieldValues;
    const phoneNumber = customerPhoneStringToInputValue(mobile).phone;

    if (formState.submitCount && !email && !phoneNumber) {
      return true;
    }
    return false;
  }, [fieldValues, formState.submitCount]);

  const onSubmit = async (data: FieldValues) => {
    if (entity && data && formFields) {
      const newCustomer = processCustomerFormsToApi(formFields, data);
      const response = await addNewCustomer({
        entityId: entity.id,
        fields: { fields: newCustomer },
        meta: {
          successMessage: t({
            id: "feedback.customer-added",
            defaultMessage: "Customer has been added",
          }),
        },
      });

      if (response && "data" in response) {
        handleClose();
        onSuccess?.(response.data.data);
      }
    }
  };
  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      initialFocus={initialFocus}
      size="auto"
      className="w-[560px]"
    >
      <div
        className={cx("flex h-[511px] w-full items-center justify-center", {
          hidden: !isLoading,
        })}
      >
        <LoadingSpinner />
      </div>
      <div
        className={cx("w-[560px]", {
          hidden: isLoading,
        })}
      >
        <header
          className="relative z-10 flex shrink-0 items-center justify-between border-[1px] border-x-0 border-t-0 border-solid border-dark-100 px-6"
          style={{
            height: 60,
          }}
        >
          <div className="flex items-center gap-2.5">
            <div className="flex items-center gap-2">
              <span className="max-w-[250px] truncate text-base leading-4 font-semibold tracking-[0.16px] text-dark">
                {t({
                  id: "tasks-details-page.add-new-customer",
                  defaultMessage: "Add New Customer",
                })}
              </span>
            </div>
          </div>
          <PlainButton onClick={handleClose}>
            <CloseIcon />
          </PlainButton>
        </header>

        <div className="mt-12 flex w-[560px] flex-col gap-10 px-14">
          {Object.keys(fieldsData).map((key) => {
            const field = fieldsData[key] as FormField;
            const { email, mobile } = fieldValues;
            const phoneNumber = customerPhoneStringToInputValue(mobile).phone;

            if (!field) return null;
            field.required = true;
            if (email && field.type === "mobile" && !field.is_cust_field) {
              field.required = false;
            }
            if (
              phoneNumber &&
              !email &&
              field.type === "email" &&
              !field.is_cust_field
            ) {
              field.required = false;
            }
            return (
              <div key={field.id}>
                <CustomerFormField
                  field={field}
                  section="personal"
                  register={register}
                  control={control}
                  value={formParams[field.id]}
                  invalid={
                    (formState.submitCount && isFieldInvalid(field.id)) || false
                  }
                  error={formState.errors[field.id] as FieldError}
                  variant="task-dialog"
                />
                {field.type !== "text" && shouldShowAddPhoneOrMail && (
                  <span className="text-xs text-gradients-danger">
                    {t({
                      id: "tasks-details-page.add-phone-or-mail-to-proceed",
                      defaultMessage: "Add phone number or email to proceed",
                    })}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <div className="my-12 flex w-full items-center justify-center">
          <Button
            className="w-[300px]"
            onClick={handleSubmit(onSubmit)}
            isDisabled={isCreatingNewCustomer}
          >
            {t({
              id: "tasks-details-page.add-customer",
              defaultMessage: "Add Customer",
            })}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
